import { AIChatTurn, ILiveTokenResponse } from "../../store/@types/ai";
import type { IApiResponse } from "../../store/@types/api-response";
import type {
  ICreateComplianceApplication,
  ICreateComplianceApplicationData,
} from "../../store/@types/compliance";
import type { IDocument } from "../../store/@types/document";
import axiosInstance from "../../utils/axiosInstance";
import { notify } from "../../components/alerts";
import { getErrorMessage } from "./apiError";

const aiEndpoint = "/compliance/api/v1/ai";
const complianceAIEndpoint = "/compliance/api/v1/compliance/ai";
const applicationEndpoint = "/compliance/api/v1/compliance";

const chatBody = (message: string, history: AIChatTurn[]) => ({
  message,
  history,
  mode: "chat" as const,
});

export const send_site_chat = async (
  message: string,
  history: AIChatTurn[],
): Promise<string> => {
  const response = await axiosInstance.post<{
    success: boolean;
    reply?: string;
    message?: string;
  }>(`${aiEndpoint}/chat`, chatBody(message, history));

  if (!response.data.success || !response.data.reply) {
    throw new Error(
      response.data.message || "The assistant could not reply right now.",
    );
  }

  return response.data.reply;
};

export const mint_live_token = async (): Promise<ILiveTokenResponse> => {
  const response = await axiosInstance.post<ILiveTokenResponse>(
    `${aiEndpoint}/live-token`,
  );

  if (!response.data.success || !response.data.token) {
    throw new Error(
      response.data.message || "Live AI is not configured right now.",
    );
  }

  return response.data;
};

export const ask_compliance_ai = async (
  question: string,
  history: AIChatTurn[],
): Promise<string> => {
  const response = await axiosInstance.post<{
    success: boolean;
    reply?: string;
    message?: string;
  }>(`${complianceAIEndpoint}/ask`, { question, history });

  if (!response.data.success || !response.data.reply) {
    throw new Error(
      response.data.message || "The assistant could not reply right now.",
    );
  }

  return response.data.reply;
};

export const ai_assistant_chat = async (
  question: string,
  history: AIChatTurn[],
): Promise<string> => {
  const response = await axiosInstance.post<{
    success: boolean;
    reply?: string;
    message?: string;
  }>(`${aiEndpoint}/compliance-ask`, { question, history });

  if (!response.data.success || !response.data.reply) {
    throw new Error(
      response.data.message || "The assistant could not reply right now.",
    );
  }

  return response.data.reply;
};

export const create_compliance_application = async (
  data: ICreateComplianceApplication,
): Promise<ICreateComplianceApplicationData | null> => {
  try {
    const response = await axiosInstance.post<
      IApiResponse<ICreateComplianceApplicationData>
    >(`${applicationEndpoint}/applications`, data);

    if (!response.data.success || !response.data.data?.id) {
      throw new Error(
        response.data.message ||
          "The application was created without a valid ID.",
      );
    }

    notify({
      type: "success",
      message:
        response.data.message || "Compliance application created successfully.",
    });
    return response.data.data;
  } catch (error) {
    notify({
      type: "error",
      message: getErrorMessage(
        error,
        "Failed to create compliance application.",
      ),
    });
    return null;
  }
};

export const generate_document = async (
  applicationId: number,
): Promise<IDocument[] | null> => {
  try {
    const response = await axiosInstance.post<
      IApiResponse<{ generated_documents?: IDocument[] }>
    >(`${applicationEndpoint}/applications/${applicationId}/generate-documents`);

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to generate documents.",
      );
    }

    return response.data.data?.generated_documents ?? [];
  } catch (error) {
    notify({
      type: "error",
      message: getErrorMessage(error, "Failed to generate documents."),
    });
    return null;
  }
};

export const aiErrorMessage = (error: unknown, fallback: string): string =>
  getErrorMessage(error, fallback);
